// 夜空：今夜見える惑星（端末内計算）と流星群の表
import { Body, Equator, Horizon, Illumination, Observer } from 'astronomy-engine';

const DIRS = ['北', '北東', '東', '南東', '南', '南西', '西', '北西'];
export function dirName(azDeg: number): string {
  return DIRS[Math.round((((azDeg % 360) + 360) % 360) / 45) % 8];
}

const PLANETS: { body: Body; name: string }[] = [
  { body: Body.Venus, name: '金星' },
  { body: Body.Jupiter, name: '木星' },
  { body: Body.Mars, name: '火星' },
  { body: Body.Saturn, name: '土星' },
  { body: Body.Mercury, name: '水星' },
];

export type VisiblePlanet = {
  name: string;
  time: Date;      // いちばん高く見える時刻（暗い時間帯の中で）
  altitude: number;
  direction: string;
  magnitude: number;
  when: '宵' | '夜半' | '明け方';
};

function altAz(body: Body, t: Date, obs: Observer) {
  const eq = Equator(body, t, obs, true, true);
  const h = Horizon(t, obs, eq.ra, eq.dec, 'normal');
  return { alt: h.altitude, az: h.azimuth };
}

function whenLabel(t: Date): '宵' | '夜半' | '明け方' {
  const h = t.getHours();
  if (h >= 15 && h < 22) return '宵';
  if (h >= 22 || h < 3) return '夜半';
  return '明け方';
}

/** now から24時間以内で最初の「暗い時間帯」（太陽高度 -8° 以下）を15分刻みで調べる */
export function getVisiblePlanets(now: Date, lat: number, lng: number): VisiblePlanet[] {
  const obs = new Observer(lat, lng, 0);
  const STEP = 15 * 60 * 1000;
  const dark: Date[] = [];
  let started = false;
  for (let i = 0; i <= 96; i++) {
    const t = new Date(now.getTime() + i * STEP);
    const isDark = altAz(Body.Sun, t, obs).alt <= -8;
    if (isDark) { dark.push(t); started = true; } else if (started) break;
  }
  if (!dark.length) return [];

  const result: VisiblePlanet[] = [];
  for (const p of PLANETS) {
    let best: { t: Date; alt: number; az: number } | null = null;
    for (const t of dark) {
      const { alt, az } = altAz(p.body, t, obs);
      if (alt >= 10 && (!best || alt > best.alt)) best = { t, alt, az };
    }
    if (!best) continue;
    const mag = Illumination(p.body, best.t).mag;
    if (mag > 1.6) continue; // 街中でも見つけやすい明るさに限る
    result.push({
      name: p.name, time: best.t, altitude: Math.round(best.alt),
      direction: dirName(best.az), magnitude: Math.round(mag * 10) / 10, when: whenLabel(best.t),
    });
  }
  return result.sort((a, b) => a.magnitude - b.magnitude);
}

// 主な流星群の極大（毎年ほぼ同じ日付。年により前後1日程度ずれる）
// 出典の目安：国立天文台「ほしぞら情報」の各年の極大日
export const METEOR_SHOWERS: { name: string; month: number; day: number }[] = [
  { name: 'しぶんぎ座流星群', month: 1, day: 4 },
  { name: 'こと座流星群', month: 4, day: 22 },
  { name: 'みずがめ座η流星群', month: 5, day: 6 },
  { name: 'ペルセウス座流星群', month: 8, day: 13 },
  { name: 'オリオン座流星群', month: 10, day: 21 },
  { name: 'しし座流星群', month: 11, day: 17 },
  { name: 'ふたご座流星群', month: 12, day: 14 },
];

export type MeteorInfo = { name: string; peak: Date; daysToPeak: number; inSeason: boolean };

function sod(d: Date) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }

/** 30日以内に極大を迎える（または極大の前後2日以内の）流星群 */
export function getMeteorShower(now: Date): MeteorInfo | null {
  const today = sod(now);
  let best: MeteorInfo | null = null;
  for (const y of [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1]) {
    for (const s of METEOR_SHOWERS) {
      const peak = new Date(y, s.month - 1, s.day);
      const days = Math.round((peak.getTime() - today.getTime()) / 86400000);
      if (days < -2 || days > 30) continue;
      if (!best || days < best.daysToPeak || best.daysToPeak < -2) {
        best = { name: s.name, peak, daysToPeak: days, inSeason: days >= -2 && days <= 2 };
      }
    }
  }
  return best;
}
